import React from 'react';
import { connect } from 'react-redux';
import { Action, Dispatch } from 'redux';
import Location from '@solvhealth/types/interfaces/Location';
import { OcrData } from '@solvhealth/types/interfaces/OcrData';
import { requireNamespaces } from '~/locale/i18n';
import { imageDataClear, imageDataSetBack, imageDataSetFront } from '../../actions/imageData';
import {
  clearCardData,
  clearFormData,
  scanCompletedBack,
  scanCompletedFront,
  scanStartedBack,
  scanStartedFront,
  setCardIdBack,
  setCardIdFront,
  setOcrDataBack,
  setOcrDataFront,
} from '../../actions/addInsurance';
import { setRuntimeVariable } from '../../actions/runtime';
import AddInsurance from '../../components/AddInsurance';
import {
  ACUANT_POST,
  DENTAL_SKIP_COPY_VARIABLE,
  FETCH_INSURERS_LIST,
  INSURANCE_TYPES,
  SUBMIT_DENTAL_INSURANCE_FORM,
  SUBMIT_INSURANCE_FORM,
} from '../../constants/index';
import { getCurrentUser } from '../../core/util/currentUser';
import { getNextRouteAfterInsurance } from '../../core/util/bookingFlowRouting';
import {
  getInitialValues,
  InsuranceFormShape,
} from '../../components/AddInsurance/util/insuranceForm';
import { analyticsTrackEvent } from '../../core/analytics';
import { INSURANCE_FILE_UPLOAD } from '../../core/analytics/events';
import AddDentalInsurance from '../../components/AddDentalInsurance';
import { FETCH_MARKETPLACE_INSURERS } from '../../sagas/marketplaceInsurers';
import { safeGet } from '../../core/util/object';
import { unsetCookie } from '../../core/util/cookies';
import { CookieNames } from '../../config/index';
import {
  getCurrentlySelectedLocationFromState,
  isPhotoOnlyInsuranceFlow,
} from '../../core/util/location';
import { AcuantPostSagaAction } from '../../components/AddInsurance/sagas/insuranceUpload';
import { ImagesSaga, ImageType, UploadImageSagaAction } from '../../sagas/images';
import {
  getNewBookingLocale,
  getPrimaryPatientFirstName,
  getPrimaryPatientFullName,
} from '../../selectors/newBooking';
import { I18NextLangFollowsNewBookingLang } from '~/components/util/I18NextLangFollowsRedux';
import { getIsFamilyVisitActivatedInBookingWidget } from '~/ducks/bookingWidgetFamilyBooking';
import { AddInsuranceOrigins } from '~/core/util/bookingFlowRouting';
import { InsurersState } from '~/reducers/insurers';
import { getLocationGeneralConfig } from '~/core/dapi/generalConfig';

const mapStateToProps = (state: any, ownProps: any) => ({
  locale: getNewBookingLocale(state),
  location: getCurrentlySelectedLocationFromState(state),
  insurers: state.insurers,
  newBooking: state.newBooking,
  accountSummary: state.account.summary,
  initialValues: getInitialValues(state),
  insuranceCoverage: state.npi,
  addInsuranceFlow: ownProps.origin,
  currentUser: getCurrentUser(state),
  isEditFlow: state.runtime.isEditFlow,
  marketplaceInsurers: state.marketplaceInsurers.insurers || [],
  requestedInsurance: safeGet(state.searchPreferences)('requestedInsurance'),
  insuranceCard: {
    front: {
      base64: state.imageData.front,
      scanStarted: state.addInsurance.front.scanStarted,
      scanCompleted: state.addInsurance.front.scanCompleted,
      error: state.addInsurance.front.error,
    },
    back: {
      base64: state.imageData.back,
      scanStarted: state.addInsurance.back.scanStarted,
      scanCompleted: state.addInsurance.back.scanCompleted,
      error: state.addInsurance.back.error,
    },
  },
  passThrough: getNextRouteAfterInsurance(
    getCurrentlySelectedLocationFromState(state),
    ownProps.origin,
    ownProps.query
  ),
  showSkipCopy: state.runtime[DENTAL_SKIP_COPY_VARIABLE],
  isFamilyVisitActivatedInBookingWidget: getIsFamilyVisitActivatedInBookingWidget(state),
  primaryPatientFullName: getPrimaryPatientFullName(state),
  primaryPatientFirstName: getPrimaryPatientFirstName(state),
});

export const getAddInsuranceActions = (imageType: ImageType) => {
  if (imageType === ImageType.HealthInsuranceFront) {
    return {
      onStart: scanStartedFront,
      onStop: scanCompletedFront,
      setImageId: [setCardIdFront],
      setImageData: [imageDataSetFront],
      setOcrData: setOcrDataFront,
    };
  }
  return {
    onStart: scanStartedBack,
    onStop: scanCompletedBack,
    setImageId: [setCardIdBack],
    setImageData: [imageDataSetBack],
    setOcrData: setOcrDataBack,
  };
};

export interface AddInsuranceActions {
  onStart: () => Action;
  onStop: () => Action;
  setImageId: ((id: string) => Action)[];
  setImageData: ((imgData: string) => Action)[];
  setOcrData: (value: OcrData) => Action;
}

export const healthDispatch = (dispatch: Dispatch<any>) => ({
  handleFileChange:
    (imageType: ImageType, location: Location, afterChangeActions: AddInsuranceActions) =>
    async (event: any) => {
      const files = [...event.target.files];
      if (!files || !(files.length > 0)) return;
      const imageFile = files[0];

      const trackingProperties = {
        locationId: location.id,
        packageName: location.package?.name,
        groupId: location.groups?.[0]?.group_id,
        type: imageType,
      };

      analyticsTrackEvent(INSURANCE_FILE_UPLOAD, {
        fileSizeBytes: imageFile.size,
        ...trackingProperties,
      });

      const ocrEnabled = await getLocationGeneralConfig<boolean>(
        'enable_insurance_ocr_booking_widget',
        location.id
      );

      if (!ocrEnabled || isPhotoOnlyInsuranceFlow(location)) {
        dispatch<UploadImageSagaAction>({
          imageFile,
          imageType,
          onStart: undefined,
          onStop: undefined,
          setImageData: afterChangeActions.setImageData,
          setImageId: afterChangeActions.setImageId,
          shouldReorient: true,
          type: ImagesSaga.Upload,
        });
      } else {
        dispatch<AcuantPostSagaAction>({
          imageFile,
          imageType,
          onStart: afterChangeActions.onStart,
          onStop: afterChangeActions.onStop,
          setImageData: afterChangeActions.setImageData,
          setImageId: afterChangeActions.setImageId,
          setOcrData: afterChangeActions.setOcrData,
          type: ACUANT_POST,
          trackingProperties,
        });
      }
    },

  clearData: () => {
    dispatch(clearCardData());
    dispatch(imageDataClear());
    dispatch(clearFormData());
  },

  clearCardData: () => {
    // mobile use case, go back to re-upload card, needs to clear only card data
    dispatch(clearCardData());
    dispatch(imageDataClear());
  },

  fetchInsurersList: (stateCode: any) =>
    dispatch({
      type: FETCH_INSURERS_LIST,
      stateCode,
    }),

  clearEditFlow: () => dispatch(setRuntimeVariable({ name: 'isEditFlow', value: false })),

  submitInsuranceInformation: (
    flow: AddInsuranceOrigins,
    form: InsuranceFormShape,
    location: Location,
    currentUser: ReturnType<typeof getCurrentUser>,
    insurers: InsurersState
  ) => {
    unsetCookie(CookieNames.INSURANCE_TUTORIAL);
    dispatch(setRuntimeVariable({ name: 'isEditFlow', value: false }));
    dispatch({
      type: SUBMIT_INSURANCE_FORM,
      flow,
      form,
      location,
      currentUser,
      insurers,
    });
  },
});

const dentalDispatch = (dispatch: Dispatch<any>) => ({
  handleFileChange: (imageType: any) => (event: any) => {
    const files = [...event.target.files];
    // @ts-expect-error ts-migrate(2365) FIXME: Operator '>' cannot be applied to types 'boolean' ... Remove this comment to see the full error message
    if (!files || !files.length > 0) return;
    const imageFile = files[0];
    analyticsTrackEvent(INSURANCE_FILE_UPLOAD, {
      fileSizeBytes: imageFile.size,
    });

    let onStart;
    let onStop;
    let setImageData;
    let setImageId;
    if (imageType === ImageType.DentalInsuranceFront) {
      onStart = scanStartedFront;
      onStop = scanCompletedFront;
      setImageData = [imageDataSetFront];
      setImageId = [setCardIdFront];
    } else {
      onStart = scanStartedBack;
      onStop = scanCompletedBack;
      setImageData = [imageDataSetBack];
      setImageId = [setCardIdBack];
    }

    dispatch<UploadImageSagaAction>({
      imageFile,
      imageType,
      onStart,
      onStop,
      setImageData,
      setImageId,
      shouldReorient: true,
      type: ImagesSaga.Upload,
    });
  },

  fetchMarketplaceInsurers() {
    dispatch({ type: FETCH_MARKETPLACE_INSURERS });
  },

  clearData: () => {
    dispatch(clearCardData());
    dispatch(imageDataClear());
    dispatch(clearFormData());
  },

  clearCardData: () => {
    // mobile use case, go back to re-upload card, needs to clear only card data
    dispatch(clearCardData());
    dispatch(imageDataClear());
  },

  fetchInsurersList: (stateCode: any) =>
    dispatch({
      type: FETCH_INSURERS_LIST,
      stateCode,
    }),

  submitInsuranceInformation: (
    flow: any,
    form: any,
    location: any,
    currentUser: any,
    insurers: any,
    insuranceType = 'medical'
  ) => {
    const getEventType = () => {
      switch (insuranceType) {
        case INSURANCE_TYPES.vision:
          return SUBMIT_DENTAL_INSURANCE_FORM;
        case INSURANCE_TYPES.dental:
          return SUBMIT_DENTAL_INSURANCE_FORM;
        case INSURANCE_TYPES.medical:
        default:
          return SUBMIT_INSURANCE_FORM;
      }
    };

    dispatch({
      type: getEventType(),
      flow,
      form,
      location,
      currentUser,
      insurers,
    });
  },
});

export const ConnectedAddHealthInsurance = connect(mapStateToProps, healthDispatch)(AddInsurance);

const ConnectedAddDentalInsurance = connect(mapStateToProps, dentalDispatch)(AddDentalInsurance);

export default (context: any) => {
  const { params, query } = context;
  const insuranceType = params.insuranceType || INSURANCE_TYPES.medical;
  const Connected =
    insuranceType === INSURANCE_TYPES.dental
      ? ConnectedAddDentalInsurance
      : ConnectedAddHealthInsurance;

  return {
    chunks: ['addInsurance'],

    component: (
      <I18NextLangFollowsNewBookingLang>
        <Connected origin={params.origin} query={query} />
      </I18NextLangFollowsNewBookingLang>
    ),
    accountWrapper: {
      loginRequired: false,
      nonBlocking: true,
    },
    ...requireNamespaces('addInsurance', 'common'),
  };
};

if (module.hot) {
  module.hot.accept();
}
